import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Page, KeyVal } from 'equasion-lib';
import ZonePickerModal, {
  describeUnits,
  useZoneScanOnLoad
} from '@/lib/shared-coms/ZonePickerModal';
import ActionTopBar from '@/lib/shared-coms/ActionTopBar';
import { FiArrowDown, FiCheckCircle, FiEdit } from 'react-icons/fi';
import ProductInLocation from '@/lib/shared-coms/ProductInLocation';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import './StockUnserialized.scss';
import { useRootStore } from '@/lib/providers/RootProvider';


// Brings quarantined OTC/UPC product back into stock. The source can be any
// zone holding quarantined units — quarantine zones for the current flow, or
// stock zones holding legacy in-place quarantined inventory.
const StockUnserialized = ({
  stockUnserializedItems,
  getZoneInventoryBalances,
  zoneInventoryBalances = []
}) => {
  const companyId = useRootStore((state) => state.selectedCompany.id);
  const [stockAmounts, setStockAmounts] = useState([]);
  const [modalZoneType, setModalZoneType] = useState(null);
  const navigate = useNavigate();
  const [stocked, setStocked] = useState(false);
  const [fromZoneId, setFromZoneId] = useState(null);
  const [fromZoneName, setFromZoneName] = useState('');
  const [stockZoneId, setStockZoneId] = useState(null);
  const [stockZoneName, setStockZoneName] = useState('');
  const isStockZoneModal = modalZoneType === 'Stock';
  // The screen starts at the zone holding the quarantined units, so that is the scanner that opens
  // with it. The stock zone opens on a tap, once the quantities are in.
  const { tapPrompt, pickerProps } = useZoneScanOnLoad(
    () => setModalZoneType('From'),
    !fromZoneId
  );
  const stockingUnits = describeUnits(stockAmounts, zoneInventoryBalances);

  const submitStockUnserializedItems = async () => {
    try {
      await stockUnserializedItems({
        variables: {
          companyId,
          stockZoneId,
          stockUnserializedItemsAttributes: stockAmounts
            .filter(({ quantity }) => quantity > 0)
            .map(({ id, quantity }) => ({
              zoneInventoryBalanceId: id,
              quantity
            }))
        }
      });
      setStocked(true);
    } catch (e) {
      console.log(`ERROR STOCKING PRODUCTS: ${e}`);
    }
  };

  useEffect(() => {
    if (zoneInventoryBalances) {
      setStockAmounts(
        zoneInventoryBalances.map(({ id }) => ({
          id,
          quantity: 0
        }))
      );
    }
  }, [zoneInventoryBalances]);

  const hasAnyQuantity = stockAmounts.some(({ quantity }) => quantity > 0);

  return (
    <Page className="stock stock-unserialized action-page">
      <ActionTopBar action="Stock Unserialized" backPath="/" />
      <div className="action-page-body">
        <div
          className={['from-zone', ...(!fromZoneId ? ['active'] : [])].join(' ')}
          onClick={() => setModalZoneType('From')}
        >
          <FiEdit />
          <h1>From Zone</h1>
          <h2 className={!fromZoneName ? 'zone-placeholder' : ''}>
            {fromZoneName || tapPrompt}
          </h2>
        </div>
        {!!fromZoneId && !zoneInventoryBalances.length && (
          <div className="empty-zone">No quarantined items in zone</div>
        )}
        {zoneInventoryBalances.map((zoneInventoryBalance) => {
          const { id, lot, quarantined, product } = zoneInventoryBalance;
          const { quantity: stockQuantity } =
            stockAmounts.find(({ id: amountId }) => id === amountId) || [];
          return (
            <ProductInLocation
              key={id}
              product={product}
              lot={lot}
              quantity={quarantined}
              productId={id}
              newQuantity={stockQuantity}
              actionType="Stock"
              setNewQuantity={(value) =>
                setStockAmounts(
                  stockAmounts.map((amount) =>
                    id === amount.id
                      ? {
                          ...amount,
                          quantity: value
                            ? Math.min(parseInt(value), quarantined)
                            : parseInt(value)
                        }
                      : amount
                  )
                )
              }
            />
          );
        })}
        <FiArrowDown className="stock-unserialized__arrow" aria-hidden />
        <div
          className={[
            'stock-zone',
            ...(!!fromZoneId && !stockZoneId ? ['active'] : [])
          ].join(' ')}
          onClick={() => !!fromZoneId && setModalZoneType('Stock')}
        >
          {!!fromZoneId && <FiEdit />}
          <h1>Stock Zone</h1>
          <h2 className={!stockZoneName ? 'zone-placeholder' : ''}>
            {stockZoneName || (fromZoneId ? tapPrompt : 'Select a From zone first')}
          </h2>
        </div>
        <Button
          type="primary"
          fullWidth
          className="stock-unserialized__submit"
          onClick={submitStockUnserializedItems}
          disabled={!fromZoneId || !stockZoneId || !hasAnyQuantity}
        >
          Stock Unserialized
        </Button>
      </div>
      {stocked && (
        <div className="result-panel open">
          <div className="result-message">
            <FiCheckCircle style={{ color: 'var(--green)' }} />
            <h1>Stocked Unserialized Successfully</h1>
            <p>
              The following items have been returned to stock zone{' '}
              <b>{stockZoneName}</b>
            </p>

            {stockAmounts
              .filter(({ quantity }) => quantity > 0)
              .map(({ id, quantity }) => ({
                ...(zoneInventoryBalances.find(
                  ({ id: zoneInventoryBalanceId }) => id === zoneInventoryBalanceId
                ) || {}),
                quantity
              }))
              .map(({ id: balanceId, product, lot, quantity }) => (
                <div className="stocked-products" key={balanceId}>
                  <WarehouseProductDisplay product={product} />
                  <KeyVal
                    items={[
                      {
                        key: 'Lot',
                        value: lot
                      },
                      {
                        key: 'Quantity',
                        value: quantity
                      }
                    ]}
                  />
                </div>
              ))}
          </div>
          <div className="result-actions">
            <Button type="primary" fullWidth onClick={() => navigate('/')}>
              Done
            </Button>
          </div>
        </div>
      )}
      <ZonePickerModal
        className="stock-zone-modal"
        open={!!modalZoneType}
        {...pickerProps(() => setModalZoneType(null))}
        title={`Select ${modalZoneType} zone:`}
        companyId={companyId}
        // The source can be any zone holding quarantined units (see the note above the
        // component), so only the destination is restricted to stock zones.
        zoneTypes={isStockZoneModal ? ['stock'] : undefined}
        scanTitle={
          isStockZoneModal ? 'Scan stock zone' : 'Scan the zone holding the quarantined items'
        }
        scanHint={
          isStockZoneModal
            ? `Returning ${stockingUnits || 'quarantined items'} from ${fromZoneName} to stock. ` +
              'Scan the label on the stock zone they are going into.'
            : 'Stock unserialized: scan the label on the zone holding the quarantined items. ' +
              'You choose the quantities next.'
        }
        onZoneChosen={({ id, display }) => {
          if (isStockZoneModal) {
            setStockZoneId(id);
            setStockZoneName(display);
          } else {
            setFromZoneId(id);
            setFromZoneName(display);
            getZoneInventoryBalances({ variables: { companyId, zoneId: id } });
          }
        }}
      />
    </Page>
  );
};

export default StockUnserialized;
